import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import './services.scss';
import Seo, { serviceSchema } from '../components/seo/Seo';
import { findService, getServiceContent, servicePath, SERVICES } from '../content/services';
import { servicesRoot } from '../lib/i18n-routes';
import dataProject from '../assets/fake-data/dataProject';
import { CONTACT } from '../lib/contact';
import LeadForm from '../components/lead-form';

function useLang() {
    const { i18n } = useTranslation();
    return i18n.language && i18n.language.startsWith('ru') ? 'ru' : 'uz';
}

function Eyebrow({ children }) {
    return <p className="h8 sub-title">{children}</p>;
}

/**
 * Rasm hali yaratilmagan bo'lsa (kontentda `prompt` bor, fayl yo'q) —
 * buzilgan ikonka o'rniga blokni butunlay yashiramiz.
 */
function SafeFigure({ image, className, priority = false }) {
    const [broken, setBroken] = useState(false);
    if (!image || broken) return null;

    return (
        <figure className={className}>
            <img
                src={image.src}
                alt={image.alt}
                width="1400"
                height="875"
                loading={priority ? 'eager' : 'lazy'}
                fetchpriority={priority ? 'high' : undefined}
                decoding={priority ? 'sync' : 'async'}
                onError={() => setBroken(true)}
            />
            {image.caption ? <figcaption>{image.caption}</figcaption> : null}
        </figure>
    );
}

/** Portfolio slug'lari bo'yicha namunalar */
function Samples({ block, lang }) {
    const items = (block?.slugs || [])
        .map((slug) => dataProject.find((p) => p.slug === slug))
        .filter(Boolean);

    if (!items.length) return null;

    return (
        <section className="svc-section svc-samples">
            <div className="container">
                <div className="tf-title st2 mb-40">
                    <Eyebrow>{block.eyebrow}</Eyebrow>
                    <h2 className="title">{block.h}</h2>
                    {block.sub ? <p className="svc-sub">{block.sub}</p> : null}
                </div>

                <div className="svc-samples__grid">
                    {items.map((item) => (
                        <a
                            key={item.slug}
                            className="project-card"
                            href={item.url || '#portfolio'}
                            target={item.url ? '_blank' : undefined}
                            rel={item.url ? 'noreferrer' : undefined}
                        >
                            <img className="project-card__shot" src={item.img} alt={item.title[lang]} width="1400" height="875" loading="lazy" />
                            <div className="project-card__body">
                                <h3 className="project-card__title">{item.title[lang]}</h3>
                                <div className="project-card__meta">{item.category[lang]}</div>
                            </div>
                        </a>
                    ))}
                </div>
            </div>
        </section>
    );
}

function Faq({ block }) {
    const [open, setOpen] = useState(0);
    if (!block?.items?.length) return null;

    return (
        <section className="svc-section svc-faq">
            <div className="container">
                <div className="tf-title st2 mb-40">
                    <Eyebrow>{block.eyebrow}</Eyebrow>
                    <h2 className="title">{block.h}</h2>
                    {block.sub ? <p className="svc-sub">{block.sub}</p> : null}
                </div>

                <dl className="svc-faq__list">
                    {block.items.map((item, idx) => (
                        <div className={`svc-faq__item${open === idx ? ' is-open' : ''}`} key={item.q}>
                            <dt>
                                <button type="button" onClick={() => setOpen(open === idx ? -1 : idx)}>
                                    <span>{item.q}</span>
                                    <i aria-hidden="true">{open === idx ? '–' : '+'}</i>
                                </button>
                            </dt>
                            <dd>{item.a}</dd>
                        </div>
                    ))}
                </dl>
            </div>
        </section>
    );
}

function ServicePage() {
    const { slug } = useParams();
    const lang = useLang();
    const { t } = useTranslation();

    const service = findService(slug);
    const content = service ? getServiceContent(service.slug, lang) : null;

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [slug]);

    const related = useMemo(() => {
        if (!content?.related) return [];
        return content.related
            .map((s) => SERVICES.find((x) => x.slug === s))
            .filter(Boolean)
            .map((s) => ({ ...s, content: getServiceContent(s.slug, lang) }))
            .filter((s) => s.content);
    }, [content, lang]);

    if (!service || !content) return <Navigate to="/404" replace />;

    const path = servicePath(service, lang);
    const altPath = servicePath(service, lang === 'ru' ? 'uz' : 'ru');
    const rootPath = servicesRoot(lang);
    const hero = content.images?.[0];

    return (
        <main className="service-page">
            <Seo
                title={content.meta.title}
                description={content.meta.description}
                keywords={content.meta.keywords}
                lang={lang}
                path={path}
                altPath={altPath}
                image={hero?.src}
                schema={serviceSchema({
                    content,
                    path,
                    lang,
                    servicesLabel: t('services'),
                    servicesPath: rootPath,
                })}
            />

            {/* ── Hero ───────────────────────────────────────────── */}
            <section className="svc-hero">
                <div className="container">
                    <nav className="svc-crumbs" aria-label="breadcrumb">
                        <Link to="/">Innobek</Link>
                        <span aria-hidden="true">/</span>
                        <Link to={rootPath}>{t('services')}</Link>
                        <span aria-hidden="true">/</span>
                        <span>{content.hero.h1}</span>
                    </nav>

                    <div className="svc-hero__grid">
                        <div className="svc-hero__text">
                            <span className="brand-asterisk svc-hero__mark">&#10033;</span>
                            <Eyebrow>{content.hero.eyebrow}</Eyebrow>
                            <h1 className="svc-hero__title">{content.hero.h1}</h1>
                            <p className="svc-hero__lead">{content.hero.lead}</p>

                            <div className="svc-hero__btns">
                                <a href={CONTACT.phoneHref} className="tf-button btn-effect">
                                    <span className="effect">{content.hero.ctaPrimary}</span>
                                </a>
                                <a href="#svc-process" className="tf-button btn-effect ghost">
                                    <span className="effect">{content.hero.ctaSecondary} &#8599;</span>
                                </a>
                            </div>

                            {content.hero.proof?.length ? (
                                <ul className="svc-proof">
                                    {content.hero.proof.map((p) => (
                                        <li key={p}><span>+</span>{p}</li>
                                    ))}
                                </ul>
                            ) : null}
                        </div>

                        <SafeFigure image={hero} className="svc-hero__media" priority />
                    </div>
                </div>
            </section>

            {/* ── Intro + ta'rif ─────────────────────────────────── */}
            <section className="svc-section svc-intro">
                <div className="container">
                    <div className="svc-intro__grid">
                        <div>
                            <h2 className="svc-h2">{content.intro.h}</h2>
                            {content.intro.p.map((p, i) => <p key={i}>{p}</p>)}
                        </div>

                        {content.definition?.text ? (
                            <aside className="svc-definition">
                                <h3>{content.definition.term}</h3>
                                <p>{content.definition.text}</p>
                            </aside>
                        ) : null}
                    </div>
                </div>
            </section>

            {/* ── Nimalar kiradi ─────────────────────────────────── */}
            {content.included?.items?.length ? (
                <section className="svc-section svc-included">
                    <div className="container">
                        <div className="tf-title st2 mb-40">
                            <Eyebrow>{content.included.eyebrow}</Eyebrow>
                            <h2 className="title">{content.included.h}</h2>
                            {content.included.sub ? <p className="svc-sub">{content.included.sub}</p> : null}
                        </div>
                        <ul className="svc-chips">
                            {content.included.items.map((item) => (
                                <li key={item}><span>+</span>{item}</li>
                            ))}
                        </ul>
                    </div>
                </section>
            ) : null}

            {/* ── Nima beradi ────────────────────────────────────── */}
            {content.benefits?.items?.length ? (
                <section className="svc-section svc-benefits">
                    <div className="container">
                        <div className="tf-title st2 mb-40">
                            <Eyebrow>{content.benefits.eyebrow}</Eyebrow>
                            <h2 className="title">{content.benefits.h}</h2>
                            {content.benefits.sub ? <p className="svc-sub">{content.benefits.sub}</p> : null}
                        </div>
                        <div className="svc-cards">
                            {content.benefits.items.map((item) => (
                                <article className="svc-card" key={item.t}>
                                    <h3>{item.t}</h3>
                                    <p>{item.d}</p>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>
            ) : null}

            <SafeFigure image={content.images?.[1]} className="svc-figure container" />

            {/* ── Kimga mos ──────────────────────────────────────── */}
            {content.audience?.items?.length ? (
                <section className="svc-section svc-audience">
                    <div className="container">
                        <div className="tf-title st2 mb-40">
                            <Eyebrow>{content.audience.eyebrow}</Eyebrow>
                            <h2 className="title">{content.audience.h}</h2>
                            {content.audience.sub ? <p className="svc-sub">{content.audience.sub}</p> : null}
                        </div>
                        <div className="svc-cards">
                            {content.audience.items.map((item) => (
                                <article className="svc-card" key={item.t}>
                                    <h3>{item.t}</h3>
                                    <p>{item.d}</p>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>
            ) : null}

            {/* ── Narxlar ────────────────────────────────────────── */}
            {content.pricing?.tiers?.length ? (
                <section className="svc-section svc-pricing section-blue">
                    <div className="container">
                        <div className="tf-title st2 mb-40">
                            <Eyebrow>{content.pricing.eyebrow}</Eyebrow>
                            <h2 className="title">{content.pricing.h}</h2>
                            {content.pricing.sub ? <p className="svc-sub">{content.pricing.sub}</p> : null}
                        </div>

                        <div className="svc-tiers">
                            {content.pricing.tiers.map((tier) => (
                                <article className={`svc-tier${tier.featured ? ' is-featured' : ''}`} key={tier.name}>
                                    <h3>{tier.name}</h3>
                                    <p className="svc-tier__tagline">{tier.tagline}</p>
                                    <div className="svc-tier__price"><strong>{tier.price}</strong></div>
                                    <p className="svc-tier__note">{tier.priceNote}</p>
                                    <p className="svc-tier__term">{tier.term}</p>
                                    <ul>
                                        {tier.features.map((f) => <li key={f}><span>+</span>{f}</li>)}
                                    </ul>
                                    <p className="svc-tier__best">{tier.best}</p>
                                </article>
                            ))}
                        </div>

                        {content.pricing.note ? <p className="svc-pricing__note">{content.pricing.note}</p> : null}
                    </div>
                </section>
            ) : null}

            {/* ── Jarayon ────────────────────────────────────────── */}
            {content.process?.steps?.length ? (
                <section className="svc-section svc-process" id="svc-process">
                    <div className="container">
                        <div className="tf-title st2 mb-40">
                            <Eyebrow>{content.process.eyebrow}</Eyebrow>
                            <h2 className="title">{content.process.h}</h2>
                            {content.process.sub ? <p className="svc-sub">{content.process.sub}</p> : null}
                        </div>
                        <ol className="svc-steps">
                            {content.process.steps.map((step, idx) => (
                                <li key={step.t}>
                                    <span className="svc-steps__num">{String(idx + 1).padStart(2, '0')}</span>
                                    <div>
                                        <h3>{step.t}</h3>
                                        <p>{step.d}</p>
                                        {step.time ? <span className="svc-steps__time">{step.time}</span> : null}
                                    </div>
                                </li>
                            ))}
                        </ol>
                    </div>
                </section>
            ) : null}

            <SafeFigure image={content.images?.[2]} className="svc-figure container" />

            <Samples block={content.samples} lang={lang} />
            <Faq block={content.faq} />

            {/* ── Bog'liq xizmatlar ──────────────────────────────── */}
            {related.length ? (
                <section className="svc-section svc-related">
                    <div className="container">
                        <h2 className="svc-h2">{t('related-services')}</h2>
                        <div className="svc-related__grid">
                            {related.map((s) => (
                                <Link className="svc-related__card" key={s.slug} to={servicePath(s, lang)}>
                                    <h3>{s.content.hero.h1}</h3>
                                    <p>{s.content.definition?.text || s.content.meta.description}</p>
                                    <span className="svc-related__more">{t('read-more')} &#8599;</span>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            ) : null}

            {/* ── Yakuniy CTA: ariza formasi ─────────────────────── */}
            <section className="svc-section svc-cta" id="ariza">
                <div className="container">
                    <div className="svc-cta__grid">
                        <div>
                            <h2 className="svc-cta__title">{content.cta.h}</h2>
                            <p>{content.cta.p}</p>

                            <ul className="svc-cta__contacts">
                                <li>
                                    <span>+</span>
                                    <a href={CONTACT.phoneHref}>{CONTACT.phoneDisplay}</a>
                                </li>
                                <li>
                                    <span>+</span>
                                    <a href={CONTACT.telegram} target="_blank" rel="noreferrer">
                                        {CONTACT.telegramLabel}
                                    </a>
                                </li>
                            </ul>
                        </div>

                        <LeadForm source={content.hero.h1} submitLabel={content.cta.button} />
                    </div>
                </div>
            </section>
        </main>
    );
}

export default ServicePage;
